// IndexedDB module for storing and retrieving shapefiles

const DB_NAME = 'ShapefileViewerDB';
const DB_VERSION = 2; // Updated for user_id support
const STORE_NAME = 'shapefiles';

let db = null;

/**
 * Initialize the IndexedDB database
 * @returns {Promise<IDBDatabase>}
 */
export async function initDB() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = () => reject(request.error);

        request.onsuccess = () => {
            db = request.result;
            resolve(db);
        };

        request.onupgradeneeded = (event) => {
            const database = event.target.result;
            const oldVersion = event.oldVersion;

            // Create object store if it doesn't exist
            if (!database.objectStoreNames.contains(STORE_NAME)) {
                const objectStore = database.createObjectStore(STORE_NAME, {
                    keyPath: 'id',
                    autoIncrement: true
                });
                objectStore.createIndex('name', 'name', { unique: false });
                objectStore.createIndex('timestamp', 'timestamp', { unique: false });
                objectStore.createIndex('user_id', 'user_id', { unique: false });
            } else if (oldVersion < 2) {
                // Migration from version 1 to 2: Add user_id field
                const transaction = event.target.transaction;
                const objectStore = transaction.objectStore(STORE_NAME);

                // Add user_id index if it doesn't exist
                if (!objectStore.indexNames.contains('user_id')) {
                    objectStore.createIndex('user_id', 'user_id', { unique: false });
                }

                // Migrate existing records to have user_id: 'anonymous'
                objectStore.openCursor().onsuccess = (e) => {
                    const cursor = e.target.result;
                    if (cursor) {
                        const record = cursor.value;
                        if (!record.user_id) {
                            record.user_id = 'anonymous';
                            cursor.update(record);
                        }
                        cursor.continue();
                    }
                };
            }
        };
    });
}

/**
 * Save a shapefile to IndexedDB
 * If a file with the same name exists for this user, update it instead of creating a duplicate
 * @param {Object} data - The shapefile data
 * @param {string} data.name - The file name
 * @param {Object} data.geojson - The GeoJSON data
 * @param {Object} data.metadata - Optional metadata
 * @param {string} userId - User ID (defaults to 'anonymous')
 * @returns {Promise<number>} The ID of the saved or updated record
 */
export async function saveShapefile(data, userId = 'anonymous') {
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);

        // Check if a file with this name exists for this user
        const nameIndex = store.index('name');
        const nameRequest = nameIndex.getAll(data.name);

        nameRequest.onsuccess = () => {
            const existingRecords = nameRequest.result;

            // Filter by user_id to find user-specific match
            const userRecord = existingRecords.find(r => r.user_id === userId);

            if (userRecord) {
                // File with same name exists for this user - update it
                console.log(`File "${data.name}" exists for user ${userId}, updating (ID: ${userRecord.id})`);

                const updatedRecord = {
                    ...userRecord,
                    geojson: data.geojson,
                    metadata: data.metadata || userRecord.metadata,
                    timestamp: Date.now()
                };

                const updateRequest = store.put(updatedRecord);
                updateRequest.onsuccess = () => resolve(userRecord.id);
                updateRequest.onerror = () => reject(updateRequest.error);
            } else {
                // No existing file with this name for this user - create new record
                const record = {
                    name: data.name,
                    geojson: data.geojson,
                    metadata: data.metadata || {},
                    user_id: userId,
                    timestamp: Date.now()
                };

                const addRequest = store.add(record);
                addRequest.onsuccess = () => resolve(addRequest.result);
                addRequest.onerror = () => reject(addRequest.error);
            }
        };

        nameRequest.onerror = () => reject(nameRequest.error);
    });
}

/**
 * Get all shapefile metadata for a specific user (without full data)
 * @param {string} userId - User ID (defaults to 'anonymous')
 * @returns {Promise<Array>} Array of metadata objects
 */
export async function getShapefileMetadata(userId = 'anonymous') {
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const index = store.index('user_id');
        const request = index.openCursor(IDBKeyRange.only(userId));
        const metadata = [];

        request.onsuccess = (event) => {
            const cursor = event.target.result;
            if (cursor) {
                // Only extract metadata fields, not the full geojson
                metadata.push({
                    id: cursor.value.id,
                    name: cursor.value.name,
                    timestamp: cursor.value.timestamp,
                    featureCount: cursor.value.geojson?.features?.length || 0,
                    user_id: cursor.value.user_id
                });
                cursor.continue();
            } else {
                // Sort by timestamp, newest first
                metadata.sort((a, b) => b.timestamp - a.timestamp);
                resolve(metadata);
            }
        };

        request.onerror = () => reject(request.error);
    });
}

/**
 * Get a shapefile by ID
 * @param {number} id - The shapefile ID
 * @returns {Promise<Object>} The shapefile data
 */
export async function getShapefile(id) {
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(id);

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

/**
 * Update a shapefile in IndexedDB
 * @param {number} id - The shapefile ID
 * @param {Object} data - The updated shapefile data
 * @returns {Promise<void>}
 */
export async function updateShapefile(id, data) {
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);

        // First get the existing record
        const getRequest = store.get(id);

        getRequest.onsuccess = () => {
            const existingRecord = getRequest.result;
            if (!existingRecord) {
                reject(new Error(`Shapefile with id ${id} not found`));
                return;
            }

            // Update the record with new data
            const updatedRecord = {
                ...existingRecord,
                geojson: data.geojson || existingRecord.geojson,
                metadata: data.metadata || existingRecord.metadata,
                name: data.name || existingRecord.name,
                timestamp: Date.now() // Update timestamp
            };

            const putRequest = store.put(updatedRecord);
            putRequest.onsuccess = () => resolve();
            putRequest.onerror = () => reject(putRequest.error);
        };

        getRequest.onerror = () => reject(getRequest.error);
    });
}

/**
 * Delete a shapefile by ID
 * @param {number} id - The shapefile ID
 * @returns {Promise<void>}
 */
export async function deleteShapefile(id) {
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.delete(id);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
}

/**
 * Clear all shapefiles from IndexedDB
 * @returns {Promise<void>}
 */
export async function clearAllShapefiles() {
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.clear();

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
}

/**
 * Clear all shapefiles for a specific user
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export async function clearUserShapefiles(userId) {
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const index = store.index('user_id');
        const request = index.openCursor(IDBKeyRange.only(userId));

        request.onsuccess = (event) => {
            const cursor = event.target.result;
            if (cursor) {
                cursor.delete();
                cursor.continue();
            } else {
                resolve();
            }
        };

        request.onerror = () => reject(request.error);
    });
}

/**
 * Delete and recreate the database (for forcing schema upgrade)
 * WARNING: This will delete all stored data
 * @returns {Promise<void>}
 */
export async function resetDatabase() {
    return new Promise((resolve, reject) => {
        // Close current connection
        if (db) {
            db.close();
            db = null;
        }

        // Delete the database
        const deleteRequest = indexedDB.deleteDatabase(DB_NAME);

        deleteRequest.onsuccess = async () => {
            console.log('Database deleted successfully');
            // Reinitialize with new schema
            try {
                await initDB();
                console.log('Database recreated with new schema');
                resolve();
            } catch (error) {
                reject(error);
            }
        };

        deleteRequest.onerror = () => reject(deleteRequest.error);
        deleteRequest.onblocked = () => {
            console.warn('Database deletion blocked - close all tabs using this database');
            reject(new Error('Database deletion blocked'));
        };
    });
}
